/**
 * Artifact preview cache — object URLs only (never persist blobs to localStorage).
 * Fingerprint covers every render-affecting capture parameter. Bounded LRU.
 */
const MAX_CACHE = 8;

export interface ArtifactCacheEntry {
  fingerprint: string;
  objectUrl: string;
  createdAt: number;
}

export interface ArtifactFingerprintInput {
  packId: string;
  packVersion: string;
  graphJson: string;
  width?: number;
  height?: number;
  scoreText?: string;
  resultCardTheme?: "default" | "portfolio-alt";
}

const cache = new Map<string, ArtifactCacheEntry>();

export function artifactFingerprint(
  packIdOrInput: string | ArtifactFingerprintInput,
  packVersion?: string,
  graphJson?: string,
): string {
  const input: ArtifactFingerprintInput =
    typeof packIdOrInput === "string"
      ? {
          packId: packIdOrInput,
          packVersion: packVersion ?? "",
          graphJson: graphJson ?? "",
        }
      : packIdOrInput;

  const width = input.width ?? 270;
  const height = input.height ?? 480;
  const scoreText = input.scoreText ?? "Reach –";
  const theme = input.resultCardTheme ?? "default";
  const key = [
    input.packId,
    input.packVersion,
    input.graphJson,
    `w${width}`,
    `h${height}`,
    `score:${scoreText}`,
    `theme:${theme}`,
  ].join("\u001f");
  return `${input.packId}@${input.packVersion}::${fnv1a(key)}`;
}

/** Stable non-crypto hash for cache keys. */
export function fnv1a(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16);
}

export function getCachedArtifactUrl(fingerprint: string): string | null {
  const hit = cache.get(fingerprint);
  if (!hit) return null;
  // refresh LRU order
  cache.delete(fingerprint);
  cache.set(fingerprint, hit);
  return hit.objectUrl;
}

export function putCachedArtifactUrl(fingerprint: string, objectUrl: string): void {
  const existing = cache.get(fingerprint);
  if (existing) {
    if (existing.objectUrl !== objectUrl) URL.revokeObjectURL(existing.objectUrl);
    cache.delete(fingerprint);
  }
  cache.set(fingerprint, { fingerprint, objectUrl, createdAt: Date.now() });
  while (cache.size > MAX_CACHE) {
    const oldest = cache.keys().next().value as string | undefined;
    if (!oldest) break;
    const entry = cache.get(oldest);
    cache.delete(oldest);
    if (entry) URL.revokeObjectURL(entry.objectUrl);
  }
}

export function revokeArtifactCache(fingerprint?: string): void {
  if (fingerprint) {
    const entry = cache.get(fingerprint);
    if (entry) {
      URL.revokeObjectURL(entry.objectUrl);
      cache.delete(fingerprint);
    }
    return;
  }
  for (const entry of cache.values()) {
    URL.revokeObjectURL(entry.objectUrl);
  }
  cache.clear();
}

export function artifactCacheSize(): number {
  return cache.size;
}

/** Test helper — replace cache contents without leaking. */
export function resetArtifactCacheForTests(): void {
  revokeArtifactCache();
}
